import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { deliveryConfigApi } from '../../services/deliveryConfigApi';
import { DeliveryFeeConfig, RecoveryProvider } from '../../types/delivery';
import DeliveryFeeForm from '../../components/finance/DeliveryFeeForm';
import ZofeurConfigForm from '../../components/finance/ZofeurConfigForm';
import ProviderRegistryTable from '../../components/finance/ProviderRegistryTable';
import ProviderForm from '../../components/finance/ProviderForm';

/** `null` = the form is closed; `'new'` = add; a provider = edit that row. */
type FormTarget = null | 'new' | RecoveryProvider;

/**
 * #1113 — Finance → Delivery & Recovery (`?tab=delivery`), gated by the `finance` screen (A1).
 *
 * Moved here unchanged from Finance → Configuration (Spec 053 Flow F6): what Garagat charges the
 * customer for a delivery leg (threshold + fee per trip, both snapshotted onto a booking, so changes
 * reach new bookings only), and the registry of recovery providers a trip can be assigned to. There
 * are no driver profiles — a driver's name and phone are typed per assignment. Spec 072 v2 adds the
 * Zofeur card (price, free-above, emirates) between the two. The one-way delivery
 * switch (`garagat.delivery.one-way-legs-blocked`) is deliberately not shown (Maher, 2026-10-18).
 */
export default function DeliveryTab() {
  const auth = useAuth();
  const [config, setConfig] = useState<DeliveryFeeConfig | null>(null);
  const [providers, setProviders] = useState<RecoveryProvider[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      const [loadedConfig, loadedProviders] = await Promise.all([
        deliveryConfigApi.getConfig(auth.token),
        deliveryConfigApi.listProviders(auth.token),
      ]);
      setConfig(loadedConfig);
      setProviders(loadedProviders);
    } catch (err) {
      setConfig(null);
      setProviders(null);
      setError(err instanceof Error ? err.message : 'Failed to load the delivery configuration');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const onProviderSaved = () => {
    setFormTarget(null);
    setRemoveError(null);
    load();
  };

  const removeProvider = async (provider: RecoveryProvider) => {
    if (!auth.token || removingId) return;
    setRemovingId(provider.id);
    setRemoveError(null);
    try {
      await deliveryConfigApi.removeProvider(provider.id, auth.token);
      await load();
    } catch (err) {
      // What lands here is a 404 (already retired elsewhere) or a transport/auth failure —
      // whatever the API says, shown verbatim.
      setRemoveError(err instanceof Error ? err.message : `Failed to remove ${provider.name}`);
    } finally {
      setRemovingId(null);
    }
  };

  if (loading && !config) {
    return <div className="admin-loading" data-testid="delivery-loading"><div className="admin-loading-spinner"></div></div>;
  }

  if (error) {
    return (
      <div className="admin-card">
        <div className="admin-info-banner danger" role="alert" data-testid="delivery-error">{error}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={load}>Try again</button>
      </div>
    );
  }

  return (
    <div data-testid="delivery-tab">
      {config && <DeliveryFeeForm config={config} token={auth.token as string} onSaved={setConfig} />}

      {/* Spec 072 v2 — the customer's second Garagat option; loads and fails on its own. */}
      {auth.token && <ZofeurConfigForm token={auth.token} />}

      <div className="admin-card" style={{ marginTop: '1rem' }}>
        <div className="admin-card-header">
          <div>
            <h3 className="admin-card-title">Recovery providers</h3>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
              Chosen per trip when a driver is assigned — every Garagat trip names one; there is no default. Driver name and
              phone are typed per assignment, so no driver is stored here.
            </div>
          </div>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-primary" onClick={() => setFormTarget('new')}>
            Add provider
          </button>
        </div>

        <ProviderRegistryTable
          providers={providers ?? []}
          onEdit={(p) => setFormTarget(p)}
          onRemove={removeProvider}
          removingId={removingId}
          removeError={removeError}
        />
      </div>

      {formTarget !== null && (
        <ProviderForm
          provider={formTarget === 'new' ? null : formTarget}
          token={auth.token as string}
          onClose={() => setFormTarget(null)}
          onSaved={onProviderSaved}
        />
      )}
    </div>
  );
}
